import React, { useState } from "react"
import { Lock, Eye, EyeOff, ShieldCheck } from "lucide-react"

// Frontend-only gate: there is no backend to verify this against, so treat it as a
// simple deterrent to keep casual visitors out, not real security. Anyone who reads
// the deployed JS bundle can find ACCESS_CODE. Change it any time by editing
// VITE_LEADS_ACCESS_CODE in .env (falls back to the value below if that's unset).
const ACCESS_CODE = (import.meta.env.VITE_LEADS_ACCESS_CODE as string | undefined) || "AnmolLeads@2026"

export const LEADS_UNLOCK_KEY = "afb_leads_unlocked"

interface AccessGateProps {
  onUnlock: () => void
}

const AccessGate: React.FC<AccessGateProps> = ({ onUnlock }) => {
  const [code, setCode] = useState("")
  const [showCode, setShowCode] = useState(false)
  const [error, setError] = useState("")

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (code.trim().toLowerCase() === ACCESS_CODE.toLowerCase()) {
      localStorage.setItem(LEADS_UNLOCK_KEY, "true")
      setError("")
      onUnlock()
    } else {
      setError("Incorrect code. Please try again.")
    }
  }

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-4 py-16">
      <div className="w-full max-w-sm bg-white rounded-2xl shadow-xl border border-gray-100 p-8 text-center">
        <div className="w-14 h-14 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <Lock className="w-7 h-7 text-primary-600" />
        </div>
        <h1 className="text-xl font-bold text-gray-900 mb-1">Private Area</h1>
        <p className="text-sm text-gray-500 mb-6">Store owner access only. Enter your access code to continue.</p>

        <form onSubmit={handleSubmit} className="space-y-4 text-left">
          <div className="relative">
            <input
              type={showCode ? "text" : "password"}
              value={code}
              onChange={(e) => {
                setCode(e.target.value)
                if (error) setError("")
              }}
              placeholder="Access code"
              autoFocus
              className="w-full px-4 py-3 pr-11 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
            <button
              type="button"
              onClick={() => setShowCode((s) => !s)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              aria-label={showCode ? "Hide code" : "Show code"}
            >
              {showCode ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
            </button>
          </div>

          {error && <p className="text-sm text-red-600">{error}</p>}

          <button
            type="submit"
            className="w-full flex items-center justify-center gap-2 bg-primary-600 text-white px-4 py-3 rounded-lg hover:bg-primary-700 transition-colors font-medium"
          >
            <ShieldCheck className="w-5 h-5" />
            Unlock
          </button>
        </form>
      </div>
    </div>
  )
}

export default AccessGate
